
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';

const PsoriasisPage = () => {
  const symptoms = [
    '皮膚が赤く盛り上がる',
    '銀白色の鱗屑がある',
    'かゆみがある',
    '皮膚が乾燥している',
    '関節の痛みがある',
    'ストレスで悪化する'
  ];

  const causes = [
    {
      title: '免疫異常',
      description: '免疫システムの過剰反応',
      icon: 'ri-shield-cross-line'
    },
    {
      title: 'ストレス',
      description: '精神的・身体的ストレス',
      icon: 'ri-emotion-unhappy-line'
    },
    {
      title: '生活習慣',
      description: '食生活・睡眠の乱れ',
      icon: 'ri-time-line'
    },
    {
      title: '感染・外傷',
      description: '皮膚への刺激や感染',
      icon: 'ri-first-aid-kit-line'
    }
  ];

  const treatments = [
    {
      category: '漢方薬',
      items: [
        { name: '温清飲', effect: '血熱を冷まし炎症を鎮める' },
        { name: '消風散', effect: '湿熱を除き皮膚症状を改善' },
        { name: '桂枝茯苓丸', effect: '瘀血を改善し血流を良くする' }
      ]
    },
    {
      category: '生薬',
      items: [
        { name: '黄連', effect: '清熱解毒・炎症を鎮める' },
        { name: '地黄', effect: '血を補い皮膚に潤いを与える' },
        { name: '紫草', effect: '皮膚の炎症を和らげる' }
      ]
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    "name": "尋常性乾癬の漢方治療 - 一心堂薬局",
    "description": "尋常性乾癬の症状、原因、漢方での治療法について詳しく解説。一心堂薬局では体質改善による根本的な治療を行います。",
    "url": `${import.meta.env.VITE_SITE_URL}/psoriasis`,
    "mainEntity": {
      "@type": "MedicalCondition",
      "name": "尋常性乾癬",
      "alternateName": "じんじょうせいかんせん",
      "description": "皮膚が赤く盛り上がり、銀白色の鱗屑が重なる慢性の炎症性皮膚疾患",
      "possibleCause": [
        "免疫異常",
        "ストレス",
        "生活習慣",
        "感染・外傷"
      ],
      "signOrSymptom": [
        "皮膚が赤く盛り上がる",
        "銀白色の鱗屑がある",
        "かゆみがある",
        "皮膚が乾燥している"
      ]
    },
    "about": {
      "@type": "MedicalBusiness",
      "name": "一心堂薬局",
      "telephone": "0479-70-6065"
    },
    "specialty": "漢方薬学",
    "medicalAudience": {
      "@type": "Patient"
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-50 to-white">
      <SEOHead
        title="尋常性乾癬の漢方治療 - 一心堂薬局"
        description="尋常性乾癬の症状、原因、漢方での治療法について詳しく解説。一心堂薬局では体質改善による根本的な治療を行います。"
        keywords="尋常性乾癬,乾癬,漢方治療,一心堂薬局,多古町,千葉県,皮膚疾患,炎症性皮膚疾患"
        canonicalUrl={`${import.meta.env.VITE_SITE_URL}/psoriasis`}
        ogTitle="尋常性乾癬の漢方治療 - 一心堂薬局"
        ogDescription="尋常性乾癬の症状、原因、漢方での治療法について詳しく解説。体質改善による根本的な治療を行います。"
        structuredData={structuredData}
      />
      
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="max-w-md mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <i className="ri-arrow-left-line text-green-800 text-xl"></i>
              <span className="text-green-800 font-medium">戻る</span>
            </Link>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                <i className="ri-leaf-fill text-white text-sm"></i>
              </div>
              <span className="font-bold text-green-800" style={{fontFamily: 'Pacifico, serif'}}>一心堂薬局</span>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-md mx-auto text-center">
          <div className="mb-6">
            <img 
              src="https://static.readdy.ai/image/26ebacc80ad5bf356c036f7614552ca1/5e77f38d3beca8ccf6083234e190ceaf.png"
              alt="尋常性乾癬のイメージ"
              className="w-full h-48 object-cover rounded-2xl shadow-lg object-top"
            />
          </div>
          <h1 className="text-2xl font-bold text-yellow-800 mb-2">
            尋常性乾癬<br />
            <span className="text-lg">(じんじょうせいかんせん)</span>
          </h1>
          <p className="text-yellow-600 text-lg mb-4">
            漢方で体質改善
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-6">
            尋常性乾癬は、皮膚が赤く盛り上がり、銀白色の鱗屑（りんせつ）が重なる慢性の炎症性皮膚疾患です。免疫の異常によって皮膚の細胞が過剰に増えることが原因とされています。ストレスや生活習慣、肥満、感染などが悪化要因となります。漢方では、血の熱（血熱）や湿、瘀血のバランスを整え、皮膚の炎症と乾燥を鎮めることを目的とします。体の内側から炎症を和らげ、再発を防ぐための体質改善を重視します。
          </p>
        </div>
      </div>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 w-full bg-white border-t border-gray-200 z-50">
        <div className="max-w-md mx-auto">
          <div className="grid grid-cols-5 py-2">
            <Link to="/" className="flex flex-col items-center justify-center py-2 text-gray-400">
              <i className="ri-home-5-line text-lg"></i>
              <span className="text-xs mt-1">ホーム</span>
            </Link>
            <button className="flex flex-col items-center justify-center py-2 text-gray-400">
              <i className="ri-medicine-bottle-line text-lg"></i>
              <span className="text-xs mt-1">業務内容</span>
            </button>
            <button className="flex flex-col items-center justify-center py-2 text-gray-400">
              <i className="ri-leaf-line text-lg"></i>
              <span className="text-xs mt-1">漢方相談</span>
            </button>
            <button className="flex flex-col items-center justify-center py-2 text-gray-400">
              <i className="ri-map-pin-line text-lg"></i>
              <span className="text-xs mt-1">アクセス</span>
            </button>
            <button className="flex flex-col items-center justify-center py-2 text-yellow-600">
              <i className="ri-skin-2-fill text-lg"></i>
              <span className="text-xs mt-1">乾癬相談</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="h-20"></div>
    </div>
  );
};

export default PsoriasisPage;
